export { assertDocumentData } from "./document-data";
export * from "./form-configuration";
export * from "./esm-project";
import { assertDocumentData } from "./document-data";
import { legacyLookupId } from "@proof/core";
export * from "./context";
export * from "./image-rules";
export * from "./prepopulation";
export * from "./xvp";
export * from "./customization";
export * from "./statements";
export * from "./dynamic-sources";
export * from "./dynamic-http";
export * from "./dynamic-provider";
export * from "./dynamic-images";
import {
  bindLegacyStatementCatalogs,
  type LegacyStatementCatalog,
} from "./statements";
import { exportCustomization } from "./customization";
import {
  parsePrepopulationXml,
  bindPrepopulation,
  serializePrepopulationXml,
} from "./prepopulation";
import { pageflexImageRules, bindImageRuleAssets } from "./image-rules";
export interface LegacyImportOptions {
  imageAssets?: Readonly<Record<string, string>>;
  statementCatalogs?: readonly LegacyStatementCatalog[];
}
import {
  parseProject,
  fieldSchema,
  safePattern,
  pageSchema,
  outputSchema,
  type Project,
  type Field,
  type Part,
  type Values,
  displayValue,
  effectiveFields,
  datasetChoices,
} from "@proof/core";
export type LegacyRow = Record<string, unknown>;
export interface LegacyEnvelope {
  tables: Record<string, LegacyRow[]>;
  _proofStudio?: { version: 1; projects: Project[] };
  [key: string]: unknown;
}
const T = {
  project: "ESM_SOFT_PROOFING_PROJECT",
  variable: "ESM_SOFT_PROOFING_VARIABLES",
  part: "ESM_PART",
  system: "ESM_SOFT_PROOFING_SYS_VAR_VALUE",
  group: "ESM_SOFT_PROOFING_PROJECT_GROUPS",
  membership: "ESM_SOFT_PROOFING_PROJECT_GROUPS_VARIABLES",
  tab: "ESM_SOFT_PROOFING_PROJECT_TABS",
  tabGroup: "ESM_SOFT_PROOFING_PROJECT_TABS_GROUPS",
  trigger: "ESM_SOFT_PROOFING_VARIABLE_TRIGGER",
  dataset: "ESM_SOFT_PROOFING_DDL_GLOBAL_DATA_SOURCE_ID",
  form: "ESM_GLOBAL_FORMS",
  struct: "ESM_GLOBAL_FORMS_STRUCT",
  instance: "ESM_SOFT_PROOFING_FORM_INSTANCE",
  mapping: "ESM_SOFT_PROOFING_FORM_INSTANCE_VARIABLE_STRUCT",
  linkage: "ESM_SOFT_PROOFING_VARIABLE_LINKAGE",
};
const id = (prefix: string, v: unknown) => `${prefix}_${String(v)}`;
const same = (a: unknown, b: unknown) => String(a ?? "") === String(b ?? "");
const bool = (v: unknown, fallback = false) =>
  v == null
    ? fallback
    : v === true ||
      v === 1 ||
      (typeof v === "string" && ["1", "y", "true"].includes(v.toLowerCase()));
const num = (v: unknown, fallback = 0) =>
  Number.isFinite(Number(v)) && v != null ? Number(v) : fallback;
const str = (v: unknown, fallback = "") => (v == null ? fallback : String(v));
const array = <T>(v: T | T[] | undefined): T[] =>
  v === undefined ? [] : Array.isArray(v) ? v : [v];
const parseJSON = (v: unknown, fallback: unknown) => {
  try {
    return JSON.parse(str(v));
  } catch {
    return fallback;
  }
};
const types: Record<string, Field["type"]> = {
  TextBox: "text",
  TextArea: "textarea",
  RichTextArea: "richtext",
  DateTime: "datetime",
  DropDownList: "dropdown",
  DropDownText: "combobox",
  RadioButton: "radio",
  CheckBox: "checkbox",
  ImageBank: "image-list",
  ImageBankListView: "image-list",
  ImageUpload: "image",
  ImageList: "image-list",
  QRCode: "qr",
  Map: "map",
  PrePopulate: "preset",
};
const legacyTypes: Record<string, string> = {
  text: "TextBox",
  textarea: "TextArea",
  richtext: "RichTextArea",
  datetime: "DateTime",
  dropdown: "DropDownList",
  combobox: "DropDownText",
  radio: "RadioButton",
  checkbox: "CheckBox",
  "image-list": "ImageList",
  image: "ImageUpload",
  qr: "QRCode",
  map: "Map",
  preset: "PrePopulate",
};
const choices = (source: unknown) =>
  str(source)
    .split(";")
    .filter(Boolean)
    .map((v) => ({ value: v, label: v }));
/** Shared control decoding for project imports and independent global-profile snapshots. */
export function legacyGlobalFormDefinition(
  row: LegacyRow,
  structures: readonly LegacyRow[],
): Project["forms"][number] {
  return {
    id: id("form", row.GLOBAL_FORMS_ID),
    name: str(row.NAME, `Form ${row.GLOBAL_FORMS_ID}`),
    allowCreate: bool(row.IS_CREATE_ALLOWED),
    fields: structures
      .filter((s) => same(s.GLOBAL_FORMS_ID, row.GLOBAL_FORMS_ID))
      .map((s) => {
        if (s.REGEX_VALIDATOR) safePattern(str(s.REGEX_VALIDATOR));
        return fieldSchema.parse({
          id: id("struct", s.GLOBAL_FORMS_STRUCT_ID),
          key: str(s.NAME),
          label: str(s.NAME),
          type: types[str(s.TYPE)] || "text",
          default: s.DEFAULT_VALUE ?? "",
          order: num(s.RANK),
          choices: choices(s.DATASOURCE),
          required: bool(s.IS_REQUIRED),
          hidden: bool(s.IS_HIDDEN),
          editable: !bool(s.IS_LOCKED),
          choicePresentation:
            s.TYPE === "ImageBankListView" ? "thumbnails" : "select",
          validation: {
            pattern: s.REGEX_VALIDATOR ? str(s.REGEX_VALIDATOR) : undefined,
            mask: s.MASK_ID == null ? undefined : str(s.MASK_ID),
          },
          legacy: structuredClone(s),
        });
      }),
  };
}
export const knownLegacyGlobalControl = (value: unknown) =>
  value == null ||
  value === "" ||
  (typeof value === "string" && Object.hasOwn(types, value));
export function normalizeLegacy(value: unknown): LegacyEnvelope {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Expected a legacy table object");
  const raw = value as Record<string, unknown>;
  const tables = (raw.tables || raw) as Record<string, unknown>;
  const normalized: Record<string, LegacyRow[]> = {};
  for (const [name, rows] of Object.entries(tables)) {
    if (name === "_proofStudio") continue;
    if (!Array.isArray(rows)) {
      if (raw.tables) throw new Error(`Table ${name} must contain rows`);
      continue;
    }
    if (rows.some((r) => !r || typeof r !== "object" || Array.isArray(r)))
      throw new Error(`Invalid row in ${name}`);
    const bare = name.replace(/^dbo\./i, ""),
      canonical =
        Object.values(T).find(
          (known) => known.toLowerCase() === bare.toLowerCase(),
        ) || bare;
    if (Object.hasOwn(normalized, canonical))
      throw new Error("Duplicate legacy table alias");
    Object.defineProperty(normalized, canonical, {
      value: structuredClone(rows),
      enumerable: true,
      writable: true,
      configurable: true,
    });
  }
  if (!normalized[T.project])
    throw new Error("Missing ESM_SOFT_PROOFING_PROJECT table");
  return {
    ...(raw.tables ? structuredClone(raw) : {}),
    tables: normalized,
  } as LegacyEnvelope;
}
function importField(
  row: LegacyRow,
  env: LegacyEnvelope,
  options: LegacyImportOptions = {},
  warnings: string[] = [],
): Field {
  const type = types[str(row.CONTROL_TYPE)] || "text",
    lookup = num(row.DB_LOOKUP_ID),
    data = (env.tables[T.dataset] || []).find(
      (d) =>
        same(d.GLOBAL_DATA_SOURCE_ID, row.GLOBAL_DATA_SOURCE_ID) ||
        same(d.DDL_GLOBAL_DATA_SOURCE_ID ?? d.ID, row.GLOBAL_DATA_SOURCE_ID),
    );
  let source: Field["source"] = { kind: "literal" };
  if (row.GLOBAL_DATA_SOURCE_ID != null)
    source = {
      kind: "dataset",
      datasetId: id("dataset", row.GLOBAL_DATA_SOURCE_ID),
    };
  else if (row.GLOBAL_FORMS_STRUCT_ID != null)
    source = {
      kind: "form",
      structId: id("struct", row.GLOBAL_FORMS_STRUCT_ID),
    };
  else if (lookup)
    source = {
      kind: lookup >= 28 && lookup <= 30 ? "order" : "user",
      key:
        lookup === 28
          ? "ORDER_DISP_NO"
          : lookup === 29
            ? "CUSTOM_IDENTIFIER"
            : lookup === 30
              ? "PROOF_NAME"
              : `lookup_${lookup}`,
    };
  else if (row.DAM_ASSET_GROUP_ID != null)
    source = ["image", "image-list"].includes(type)
      ? {
          kind: "dam",
          providerId: "legacy",
          folderId: str(row.DAM_ASSET_GROUP_ID),
        }
      : { kind: "asset", key: str(row.DAM_ASSET_GROUP_ID) };
  else if (row.STATEMENT_GROUP_ID != null)
    source = {
      kind: "statement",
      key: str(row.STATEMENT_GROUP_ID),
      ...(num(row.STATEMENT_ID) > 0
        ? { statementId: str(row.STATEMENT_ID) }
        : {}),
    };
  const mapping = (env.tables[T.mapping] || []).find((m) =>
    same(m.VARIABLE_ID, row.VARIABLE_ID),
  );
  if (mapping)
    source = {
      kind: "form",
      instanceId: id("instance", mapping.INSTANCE_ID),
      structId: id("struct", mapping.STRUCT_ID),
    };
  const q = type === "qr" ? parseJSON(row.CONTROL_DATASOURCE, {}) : undefined;
  let presets: Field["presets"] = [];
  if (type === "preset" && row.PREPOPULATING_XML_DATA) {
    try {
      presets = parsePrepopulationXml(str(row.PREPOPULATING_XML_DATA));
    } catch (error) {
      warnings.push(
        `Variable ${row.VARIABLE_ID}: prepopulation retained without interpretation (${(error as Error).message})`,
      );
    }
  }
  const f = fieldSchema.parse({
    id: id("variable", row.VARIABLE_ID),
    key: str(row.PAGEFLEX_VARIABLE_NAME),
    label: str(row.DISPLAY_NAME, str(row.PAGEFLEX_VARIABLE_NAME, "Field")),
    type,
    ...(type === "image-list"
      ? {
          choicePresentation:
            row.CONTROL_TYPE === "ImageBankListView" ? "thumbnails" : "select",
        }
      : {}),
    order: num(row.DISPLAY_ORDER),
    help: str(row.TOOLTIP_TEXT),
    required: bool(row.REQUIRED),
    hidden: bool(row.IS_HIDDEN),
    editable: bool(row.IS_EDITABLE, true),
    system: !bool(row.IS_USER_VARIABLE, true),
    default: row.DEFAULT_VALUE_PARAMETER ?? "",
    source,
    choices:
      type === "qr" ? [] : choices(data?.DDL_VALUES ?? row.CONTROL_DATASOURCE),
    validation: {
      maxLength: row.MAX_LENGTH == null ? undefined : num(row.MAX_LENGTH),
      mask: row.MASK_ID == null ? undefined : str(row.MASK_ID),
    },
    price: num(row.PRICE),
    proofName: bool(row.FLAG_IS_PROOF_NAME),
    autoGenerated: bool(row.IS_AUTO_GENERATED),
    image: {
      maxBytes: 10000000,
      tools: [
        "crop",
        "rotate",
        "flipX",
        "flipY",
        "grayscale",
        "invert",
        "brightness",
        "contrast",
        "exposure",
        "saturation",
        "hue",
        "temperature",
        "tint",
        "sharpness",
        "blur",
        "opacity",
        "resize",
        "removeBackground",
      ].filter((t) => t !== "crop" || !bool(row.DO_NOT_USE_CROPPER)),
      fixedAspect: bool(row.USE_FIXED_ASPECT_RATIO),
      aspect: num(row.IMAGE_WIDTH, 1) / Math.max(1, num(row.IMAGE_HEIGHT, 1)),
      allowUpload: type === "image" || bool(row.ALLOW_UPLOAD),
    },
    presets,
    legacy: structuredClone(row),
  });
  if (
    type === "image-list" &&
    source.kind === "literal" &&
    !row.PREPOPULATING_XML_DATA
  ) {
    const base =
      row.OVERRIDE_VARIABLE_ID == null
        ? row
        : env.tables[T.variable]?.find((v) =>
            same(v.VARIABLE_ID, row.OVERRIDE_VARIABLE_ID),
          );
    const project = env.tables[T.project].find((p) =>
      same(p.PROJECT_ID, base?.PROJECT_ID),
    );
    if (
      project?.PROJECT_XML &&
      row.DEFAULT_VALUE_PARAMETER &&
      row.PAGEFLEX_VARIABLE_NAME
    ) {
      try {
        const rules = pageflexImageRules(
          str(project.PROJECT_XML),
          str(row.PAGEFLEX_VARIABLE_NAME),
          str(row.DEFAULT_VALUE_PARAMETER),
        );
        if (rules.length) {
          const mapped = bindImageRuleAssets(rules, options.imageAssets);
          f.choices = mapped.choices;
          f.source = {
            kind: "image-rule",
            key: str(row.DEFAULT_VALUE_PARAMETER),
          };
          f.default = "";
          if (mapped.missing.length)
            warnings.push(
              `Variable ${row.VARIABLE_ID}: ${mapped.missing.length} image rules need managed image assignments`,
            );
        }
      } catch (error) {
        warnings.push(
          `Variable ${row.VARIABLE_ID}: image rules preserved without interpretation (${(error as Error).message})`,
        );
      }
    }
  }
  if (type === "qr") {
    const raw = q as Record<string, unknown>;
    const sub = str(row.SECONDARY_CONTROL_TYPE, "Website").toLowerCase();
    const kind =
      sub === "contact"
        ? "contact"
        : sub === "email"
          ? "email"
          : sub === "phone"
            ? "phone"
            : sub === "sms"
              ? "sms"
              : sub === "location"
                ? "location"
                : "website";
    f.qr = {
      kind,
      mapping: Object.fromEntries(
        Object.entries(raw)
          .filter(
            ([k, v]) =>
              !k.toLowerCase().includes("color") &&
              (typeof v === "number" || typeof v === "string"),
          )
          .map(([k, v]) => [
            k.toLowerCase().replace("variableid", "").replace("_", ""),
            id("variable", v),
          ]),
      ),
      foreground: str(raw.foregroundColor, "#183c3c"),
      background: str(raw.backgroundColor, "transparent"),
    };
  }
  return f;
}
const withoutLegacy = (f: Field) => {
  const { legacy, ...rest } = f;
  return rest;
};
const canonicalValue = (value: unknown) =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : item,
  );
export function fromLegacy(
  value: unknown,
  options: LegacyImportOptions = {},
): {
  projects: Project[];
  envelope: LegacyEnvelope;
  warnings: string[];
} {
  assertDocumentData(value);
  const env = normalizeLegacy(value),
    warnings: string[] = [];
  const projects: Project[] = [];
  for (const row of env.tables[T.project]) {
    const columnData = parseJSON(
      row.extra_json_data ?? row.EXTRA_JSON_DATA,
      {},
    ) as LegacyRow;
    const existingExtension =
      (columnData.proofStudioVersion === 1
        ? (columnData.project as Project)
        : undefined) ||
      env._proofStudio?.projects?.find(
        (p) => p.id === id("project", row.PROJECT_ID),
      );
    if (existingExtension) {
      const restored = parseProject({
        ...existingExtension,
        legacy: {
          ...(existingExtension.legacy?.compositionSource
            ? {
                compositionSource: structuredClone(
                  existingExtension.legacy.compositionSource,
                ),
              }
            : {}),
          ...(existingExtension.legacy?.documentImport
            ? {
                documentImport: structuredClone(
                  existingExtension.legacy.documentImport,
                ),
              }
            : {}),
          projectRow: row,
          envelope: env,
          baselineFields: structuredClone(existingExtension.fields),
          baselineParts: structuredClone(existingExtension.parts),
        },
      });
      projects.push(restored);
      continue;
    }
    const pid = row.PROJECT_ID,
      projectId = id("project", pid),
      baseRows = (env.tables[T.variable] || []).filter(
        (v) =>
          same(v.PROJECT_ID, pid) &&
          v.OVERRIDE_VARIABLE_ID == null &&
          v.PART_ID == null,
      ),
      fields = baseRows.map((r) => importField(r, env, options, warnings));
    const bindPresets = (field: Field, selectorId = field.id) => {
      if (!field.presets.length) return;
      try {
        const result = bindPrepopulation(
          field.presets,
          fields,
          selectorId,
          options.imageAssets,
        );
        field.presets = result.presets;
        if (result.unknown.length)
          warnings.push(
            `Variable ${field.legacy?.VARIABLE_ID}: ${result.unknown.length} unbound prepopulation columns retained in source XML`,
          );
      } catch (error) {
        field.presets = [];
        warnings.push(
          `Variable ${field.legacy?.VARIABLE_ID}: prepopulation retained without interpretation (${(error as Error).message})`,
        );
      }
    };
    fields.forEach((field) => bindPresets(field));
    const groupRows = (env.tables[T.group] || []).filter((r) =>
      same(r.PROJECT_ID, pid),
    );
    for (const f of fields) {
      const m = (env.tables[T.membership] || []).find(
        (m) =>
          same(m.VARIABLE_ID, f.legacy?.VARIABLE_ID) &&
          groupRows.some((g) => same(g.GROUP_ID, m.GROUP_ID)),
      );
      if (m) f.groupId = id("group", m.GROUP_ID);
    }
    const associatedParts = (env.tables[T.part] || []).filter((p) =>
      same(p.SOFT_PROOFING_PROJECT_ID ?? p.PROJECT_ID, pid),
    );
    const overrideRows = (env.tables[T.variable] || []).filter(
      (v) =>
        v.PART_ID != null &&
        fields.some((f) => same(f.legacy?.VARIABLE_ID, v.OVERRIDE_VARIABLE_ID)),
    );
    const partIds = new Set([
      ...associatedParts.map((p) => str(p.PART_ID)),
      ...overrideRows.map((p) => str(p.PART_ID)),
    ]);
    const parts = [...partIds].map((partId) => {
      const raw = associatedParts.find((p) => same(p.PART_ID, partId));
      const overrides: Record<string, Partial<Field>> = {};
      for (const r of overrideRows.filter((r) => same(r.PART_ID, partId))) {
        const base = fields.find((f) =>
          same(f.legacy?.VARIABLE_ID, r.OVERRIDE_VARIABLE_ID),
        )!;
        const override = importField(r, env, options, warnings);
        bindPresets(override, base.id);
        const delta: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(withoutLegacy(override))) {
          if (k === "id" || k === "key" || k === "groupId") continue;
          if (canonicalValue(v) !== canonicalValue(base[k as keyof Field]))
            delta[k] = v;
        }
        delta.legacy = r;
        overrides[base.id] = delta;
      }
      const systemValues = Object.fromEntries(
        (env.tables[T.system] || [])
          .filter(
            (v) =>
              same(v.PART_ID, partId) &&
              fields.some((f) => same(f.legacy?.VARIABLE_ID, v.VARIABLE_ID)),
          )
          .map((v) => [
            id("variable", v.VARIABLE_ID),
            v.VARIABLE_VALUE as Values[string],
          ]),
      );
      return {
        id: id("part", partId),
        name: str(raw?.PART_NAME ?? raw?.DESCRIPTION, `Part ${partId}`),
        sku: str(raw?.DISPLAY_SKU ?? raw?.SKU, partId),
        instructions: str(raw?.SOFT_PROOFING_INSTRUCTIONS),
        overrides,
        systemValues,
      };
    });
    const dsIds = new Set(
      fields
        .filter((f) => f.source.kind === "dataset")
        .map((f) => f.source.datasetId!),
    );
    const datasets = [...dsIds].map((dsId) => {
      const raw = (env.tables[T.dataset] || []).find(
        (r) =>
          id(
            "dataset",
            r.DDL_GLOBAL_DATA_SOURCE_ID ?? r.GLOBAL_DATA_SOURCE_ID ?? r.ID,
          ) === dsId,
      );
      if (!raw)
        warnings.push(`Dataset ${dsId} is missing; retained an empty dataset`);
      return {
        id: dsId,
        name: str(raw?.NAME, dsId),
        version: 1,
        choices: choices(raw?.DDL_VALUES),
      };
    });
    const instances = (env.tables[T.instance] || []).filter((i) =>
      same(i.PROJECT_ID, pid),
    );
    const formIds = new Set(
      instances.map((i) => str(i.FORM_ID ?? i.GLOBAL_FORMS_ID)),
    );
    const forms = [...formIds].map((formId) =>
      legacyGlobalFormDefinition(
        (env.tables[T.form] || []).find((f) =>
          same(f.GLOBAL_FORMS_ID, formId),
        ) || { GLOBAL_FORMS_ID: formId },
        env.tables[T.struct] || [],
      ),
    );
    const triggers: Project["triggers"] = [];
    for (const t of (env.tables[T.trigger] || []).filter(
      (t) => same(t.PROOFING_PROJECT_ID, pid) && bool(t.ACTIVE, true),
    )) {
      const data = parseJSON(t.TRIGGER_JSON, { rules: [] }) as {
        rules: LegacyRow[];
      };
      const target = (s: unknown) =>
        str(s).startsWith("group_") ? str(s) : id("variable", s);
      for (const [i, r] of (data.rules || []).entries()) {
        triggers.push({
          id: `trigger_${t.ID}_${i}`,
          sourceId: id("variable", t.VARIABLE_ID),
          value: r.value,
          show: array(r.show as unknown[]).map(target),
          hide: array(r.hide as unknown[]).map(target),
          clear: array(r.novalue as unknown[]).map(target),
          exclude: {},
        });
        if (array(r.dataSources).length)
          warnings.push(
            `Trigger ${t.ID} option exclusions retained as opaque data; configure native exclusions before use`,
          );
      }
    }
    const extension = env._proofStudio?.projects?.find(
      (p) => p.id === projectId,
    );
    const draft: Project = parseProject({
      schemaVersion: 1,
      id: projectId,
      tenantId: "demo",
      name: str(row.PROJECT_NAME, "Imported project"),
      description: str(row.DESCRIPTION),
      instructions: str(row.CUSTOM_INSTRUCTIONS),
      fields,
      parts,
      datasets,
      groups: groupRows.map((g) => ({
        id: id("group", g.GROUP_ID),
        name: str(g.GROUP_NAME),
        column: Math.min(3, Math.max(1, num(g.COLUMN_NUMBER, 1))),
        order: num(g.ROW_NUMBER),
        visible: bool(g.VISIBLE, true),
        note: str(g.NOTE),
        instanceId:
          g.INSTANCE_ID == null ? undefined : id("instance", g.INSTANCE_ID),
      })),
      tabs: (env.tables[T.tab] || [])
        .filter((t) => same(t.PROJECT_ID, pid))
        .map((t) => ({
          id: id("tab", t.TAB_ID),
          name: str(t.TAB_NAME),
          order: num(t.ROW_NUMBER),
          groupIds: (env.tables[T.tabGroup] || [])
            .filter((m) => same(m.TAB_ID, t.TAB_ID))
            .map((m) => id("group", m.GROUP_ID)),
        })),
      triggers,
      forms,
      formInstances: instances.map((i) => ({
        id: id("instance", i.INSTANCE_ID),
        formId: id("form", i.FORM_ID ?? i.GLOBAL_FORMS_ID),
        label: str(i.DISPLAY_LABEL ?? i.NAME),
        mapping: Object.fromEntries(
          (env.tables[T.mapping] || [])
            .filter(
              (m) =>
                same(m.INSTANCE_ID, i.INSTANCE_ID) &&
                fields.some((f) => same(f.legacy?.VARIABLE_ID, m.VARIABLE_ID)),
            )
            .map((m) => [
              id("struct", m.STRUCT_ID),
              id("variable", m.VARIABLE_ID),
            ]),
        ),
      })),
      pages: extension?.pages || [
        pageSchema.parse({
          id: `page_${pid}`,
          name: "Imported layout",
          nodes: [],
        }),
      ],
      styles: extension?.styles || [],
      outputs: extension?.outputs || [
        outputSchema.parse({
          id: `output_${pid}`,
          name: str(row.HIGH_RESOLUTION_JOB, "Print PDF"),
          format: "pdf",
        }),
      ],
      settings: {
        comments: bool(row.ENABLE_PROOF_COMMENTS),
        lockProofName: bool(row.FLAG_LOCK_PROOF_NAME),
        overflowWarning: bool(row.ENABLE_TEXT_OVERFLOW_WARNING),
        overflowMessage: str(
          row.TEXT_OVERFLOW_MESSAGE,
          "Content exceeds its frame.",
        ),
      },
      legacy: {
        projectRow: row,
        envelope: env,
        warnings,
        baselineFields: structuredClone(fields),
        baselineParts: structuredClone(parts),
      },
    });
    if (!extension)
      warnings.push(
        `${draft.name}: Pageflex project files are retained by reference; binary .pf/.xdt layouts require an external Pageflex adapter or a native redesign`,
      );
    projects.push(draft);
  }
  const bound = projects.map((project) => {
    const result = bindLegacyStatementCatalogs(
      project,
      options.statementCatalogs,
    );
    warnings.push(...result.warnings);
    return result.project;
  });
  return { projects: bound, envelope: env, warnings };
}
function fieldRow(
  f: Field,
  raw: LegacyRow,
  datasets?: Map<string, number>,
  structs?: Map<string, number>,
  fields: readonly Field[] = [],
  baselinePresets?: Field["presets"],
): LegacyRow {
  return {
    ...raw,
    PAGEFLEX_VARIABLE_NAME: f.key,
    DISPLAY_NAME: f.label,
    DISPLAY_ORDER: f.order,
    CONTROL_TYPE:
      f.type === "image-list" && f.choicePresentation === "thumbnails"
        ? "ImageBankListView"
        : f.type === "image-list" &&
            f.choicePresentation === "select" &&
            raw.CONTROL_TYPE === "ImageBankListView"
          ? "ImageBank"
          : types[str(raw.CONTROL_TYPE)] === f.type
            ? raw.CONTROL_TYPE
            : legacyTypes[f.type] || "TextBox",
    DEFAULT_VALUE_PARAMETER:
      f.source.kind === "image-rule"
        ? f.source.key || ""
        : typeof f.default === "object"
          ? JSON.stringify(f.default)
          : f.default,
    MAX_LENGTH: f.validation.maxLength ?? null,
    IS_USER_VARIABLE: !f.system,
    IS_EDITABLE: f.editable,
    IS_HIDDEN: f.hidden,
    REQUIRED: f.required,
    IS_AUTO_GENERATED: f.autoGenerated,
    FLAG_IS_PROOF_NAME: f.proofName,
    TOOLTIP_TEXT: f.help,
    PRICE: f.price,
    USE_FIXED_ASPECT_RATIO: f.image.fixedAspect,
    DO_NOT_USE_CROPPER: !f.image.tools.includes("crop"),
    ALLOW_UPLOAD: f.image.allowUpload,
    GLOBAL_DATA_SOURCE_ID:
      f.source.kind === "dataset"
        ? (datasets?.get(f.source.datasetId!) ?? numericId(f.source.datasetId!))
        : null,
    STATEMENT_GROUP_ID:
      f.source.kind === "statement"
        ? (numericId(f.source.key || "") ?? null)
        : null,
    STATEMENT_ID:
      f.source.kind === "statement" && numericId(f.source.key || "")
        ? (numericId(f.source.statementId || "") ?? null)
        : null,
    DB_LOOKUP_ID: ["user", "order"].includes(f.source.kind)
      ? lookupId(f.source.key)
      : null,
    GLOBAL_FORMS_STRUCT_ID:
      f.source.kind === "form"
        ? (structs?.get(f.source.structId || "") ??
          numericId(f.source.structId || ""))
        : null,
    DAM_ASSET_GROUP_ID:
      f.source.kind === "dam"
        ? (numericId(f.source.folderId || "") ?? null)
        : f.source.kind === "asset"
          ? (numericId(f.source.key || "") ?? null)
          : null,
    CONTROL_DATASOURCE:
      f.type === "qr"
        ? JSON.stringify({
            ...(parseJSON(raw.CONTROL_DATASOURCE, {}) as object),
            ...f.qr?.mapping,
            foregroundColor: f.qr?.foreground,
            backgroundColor: f.qr?.background,
          })
        : f.choices.map((c) => c.value).join(";"),
    SECONDARY_CONTROL_TYPE:
      f.type === "qr"
        ? `${f.qr?.kind?.[0].toUpperCase()}${f.qr?.kind?.slice(1)}`
        : (raw.SECONDARY_CONTROL_TYPE ?? null),
    PREPOPULATING_XML_DATA:
      f.type === "preset"
        ? serializePrepopulationXml(
            f.presets,
            fields,
            typeof raw.PREPOPULATING_XML_DATA === "string"
              ? raw.PREPOPULATING_XML_DATA
              : undefined,
            baselinePresets,
          )
        : (raw.PREPOPULATING_XML_DATA ?? null),
  };
}
const lookupId = legacyLookupId;
const numericId = (value: string) => {
  const n = Number(value.replace(/^[^_]+_/, ""));
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
};
export function toLegacy(
  projects: Project[],
  original?: LegacyEnvelope,
): LegacyEnvelope {
  assertDocumentData(projects);
  assertDocumentData(original);
  const seed =
    original || (projects[0]?.legacy?.envelope as LegacyEnvelope | undefined);
  const env: LegacyEnvelope = seed ? structuredClone(seed) : { tables: {} };
  const table = (name: string) => (env.tables[name] ||= []);
  const next = (name: string, key: string) =>
    Math.max(0, ...table(name).map((r) => num(r[key]))) + 1;
  const native: Project[] = [];
  const upsert = (name: string, key: string, row: LegacyRow) => {
    const existing = table(name).find((r) => same(r[key], row[key]));
    if (existing) Object.assign(existing, row);
    else table(name).push(row);
  };
  for (const source of projects) {
    const p = structuredClone(source);
    const raw = (p.legacy?.projectRow || {}) as LegacyRow;
    const pid = num(
      raw.PROJECT_ID,
      numericId(p.id) || next(T.project, "PROJECT_ID"),
    );
    const current = table(T.project).find((r) => same(r.PROJECT_ID, pid));
    const projectRow = {
      ...raw,
      PROJECT_ID: pid,
      PROJECT_NAME: p.name,
      DESCRIPTION: p.description,
      ENABLE_PROOF_COMMENTS: p.settings.comments,
      FLAG_LOCK_PROOF_NAME: p.settings.lockProofName,
      ENABLE_TEXT_OVERFLOW_WARNING: p.settings.overflowWarning,
      TEXT_OVERFLOW_MESSAGE: p.settings.overflowMessage,
    };
    if (current) Object.assign(current, projectRow);
    else table(T.project).push(projectRow);
    const fieldIds = new Map<string, number>();
    const datasetIds = new Map<string, number>(),
      formIds = new Map<string, number>(),
      structIds = new Map<string, number>(),
      instanceIds = new Map<string, number>(),
      groupIds = new Map<string, number>(),
      tabIds = new Map<string, number>();
    const formStructIds = new Map<string, Map<string, number>>();
    const instanceForms = new Map(
      p.formInstances.map((instance) => [instance.id, instance.formId]),
    );
    const structsForSource = (source: Field["source"]) =>
      source.instanceId
        ? formStructIds.get(instanceForms.get(source.instanceId) || "") ||
          structIds
        : structIds;
    for (const dataset of p.datasets.filter(
      (d) =>
        !p.datasets.some(
          (other) => other.id === d.id && other.version > d.version,
        ),
    )) {
      const did =
        numericId(dataset.id) || next(T.dataset, "DDL_GLOBAL_DATA_SOURCE_ID");
      datasetIds.set(dataset.id, did);
      upsert(T.dataset, "DDL_GLOBAL_DATA_SOURCE_ID", {
        DDL_GLOBAL_DATA_SOURCE_ID: did,
        NAME: dataset.name,
        SITE_ID: raw.SITE_ID ?? 0,
        DDL_VALUES: datasetChoices(dataset, { kind: "dataset" })
          .map((c) => c.value)
          .join(";"),
        FLAG_IS_ACTIVE: true,
        FLAG_IS_IMAGE: false,
      });
    }
    for (const form of p.forms) {
      const fid = numericId(form.id) || next(T.form, "GLOBAL_FORMS_ID");
      formIds.set(form.id, fid);
      const scopedStructs = new Map<string, number>();
      formStructIds.set(form.id, scopedStructs);
      upsert(T.form, "GLOBAL_FORMS_ID", {
        GLOBAL_FORMS_ID: fid,
        COMPANY_ID: raw.COMPANY_ID ?? 0,
        NAME: form.name,
        IS_CREATE_ALLOWED: form.allowCreate,
      });
      for (const field of form.fields) {
        const candidate = numericId(field.id);
        const prior =
          candidate &&
          table(T.struct).find((row) =>
            same(row.GLOBAL_FORMS_STRUCT_ID, candidate),
          );
        const sid =
          candidate && (!prior || same(prior.GLOBAL_FORMS_ID, fid))
            ? candidate
            : next(T.struct, "GLOBAL_FORMS_STRUCT_ID");
        structIds.set(field.id, sid);
        scopedStructs.set(field.id, sid);
        upsert(T.struct, "GLOBAL_FORMS_STRUCT_ID", {
          GLOBAL_FORMS_STRUCT_ID: sid,
          GLOBAL_FORMS_ID: fid,
          NAME: field.label,
          TYPE: legacyTypes[field.type] || "TextBox",
          DEFAULT_VALUE: field.default,
          DATASOURCE: field.choices.map((c) => c.value).join(";"),
          RANK: field.order,
          IS_REQUIRED: field.required,
          IS_HIDDEN: field.hidden,
          IS_LOCKED: !field.editable,
          REGEX_VALIDATOR: field.validation.pattern ?? null,
          ACTIVE: "Y",
        });
      }
    }
    const priorInstances = table(T.instance)
      .filter((r) => same(r.PROJECT_ID, pid))
      .map((r) => r.INSTANCE_ID);
    env.tables[T.instance] = table(T.instance).filter(
      (r) => !same(r.PROJECT_ID, pid),
    );
    for (const instance of p.formInstances) {
      const iid = numericId(instance.id) || next(T.instance, "INSTANCE_ID");
      instanceIds.set(instance.id, iid);
      const existing = (seed?.tables[T.instance] || []).find((r) =>
        same(r.INSTANCE_ID, iid),
      );
      table(T.instance).push({
        ...existing,
        INSTANCE_ID: iid,
        PROJECT_ID: pid,
        FORM_ID: formIds.get(instance.formId),
        NAME: existing?.NAME ?? instance.label,
        DISPLAY_LABEL: instance.label,
      });
    }
    const originalFields = (p.legacy?.baselineFields || []) as Field[];
    const ownBase = table(T.variable).filter(
      (r) => same(r.PROJECT_ID, pid) && r.PART_ID == null,
    );
    const ownBaseIds = ownBase.map((r) => r.VARIABLE_ID);
    env.tables[T.variable] = table(T.variable).filter(
      (r) =>
        !(same(r.PROJECT_ID, pid) && r.PART_ID == null) &&
        !(
          r.PART_ID != null &&
          ownBaseIds.some((x) => same(x, r.OVERRIDE_VARIABLE_ID))
        ),
    );
    for (const f of p.fields) {
      const raw = (f.legacy || {}) as LegacyRow;
      const variableId = num(
        raw.VARIABLE_ID,
        numericId(f.id) || next(T.variable, "VARIABLE_ID"),
      );
      fieldIds.set(f.id, variableId);
      const baseline = originalFields.find((x) => x.id === f.id);
      const unchanged =
        baseline &&
        canonicalValue(withoutLegacy(f)) ===
          canonicalValue(withoutLegacy(baseline));
      table(T.variable).push(
        unchanged
          ? structuredClone(raw)
          : {
              ...fieldRow(
                f,
                raw,
                datasetIds,
                structsForSource(f.source),
                p.fields,
                baseline?.presets,
              ),
              VARIABLE_ID: variableId,
              PROJECT_ID: pid,
              PART_ID: null,
              OVERRIDE_VARIABLE_ID: null,
            },
      );
    }
    for (const part of p.parts) {
      const partId = numericId(part.id) || next(T.part, "PART_ID");
      let partRow = table(T.part).find((r) => same(r.PART_ID, partId));
      if (!partRow) {
        partRow = {
          PART_ID: partId,
          SOFT_PROOFING_PROJECT_ID: pid,
          DISPLAY_SKU: part.sku,
          PART_NAME: part.name,
        };
        table(T.part).push(partRow);
      }
      const effective = effectiveFields(p, part.id);
      // Persist only explicitly overridden variables; never write inherited definitions as part rows.
      for (const [fieldId, patch] of Object.entries(part.overrides)) {
        const f = effective.find((f) => f.id === fieldId)!;
        const patchRaw = (patch.legacy || {}) as LegacyRow;
        const baseRow = table(T.variable).find((r) =>
          same(r.VARIABLE_ID, fieldIds.get(fieldId)),
        )!;
        table(T.variable).push({
          ...fieldRow(
            f,
            { ...baseRow, ...patchRaw },
            datasetIds,
            structsForSource(f.source),
            p.fields,
            ((p.legacy?.baselineParts || []) as Part[]).find(
              (before) => before.id === part.id,
            )?.overrides[fieldId]?.presets ||
              originalFields.find((before) => before.id === fieldId)?.presets,
          ),
          VARIABLE_ID: num(
            patchRaw.VARIABLE_ID,
            next(T.variable, "VARIABLE_ID"),
          ),
          PROJECT_ID: null,
          PART_ID: partId,
          OVERRIDE_VARIABLE_ID: fieldIds.get(fieldId),
        });
      }
      env.tables[T.system] = table(T.system).filter(
        (r) =>
          !same(r.PART_ID, partId) ||
          !ownBaseIds.some((x) => same(x, r.VARIABLE_ID)),
      );
      for (const [fieldId, value] of Object.entries(part.systemValues)) {
        const existing = (seed?.tables[T.system] || []).find(
          (r) =>
            same(r.PART_ID, partId) &&
            same(r.VARIABLE_ID, fieldIds.get(fieldId)),
        );
        table(T.system).push({
          ...existing,
          PART_VARIABLE_ID:
            existing?.PART_VARIABLE_ID || next(T.system, "PART_VARIABLE_ID"),
          VARIABLE_ID: fieldIds.get(fieldId),
          PART_ID: partId,
          VARIABLE_VALUE: displayValue(value),
        });
      }
      for (const fieldId of Object.keys(part.overrides)) {
        const exported = table(T.variable).find(
          (r) =>
            same(r.PART_ID, partId) &&
            same(r.OVERRIDE_VARIABLE_ID, fieldIds.get(fieldId)),
        );
        if (exported)
          part.overrides[fieldId] = {
            ...part.overrides[fieldId],
            legacy: structuredClone(exported),
          };
      }
      part.id = id("part", partId);
    }
    const previousGroups = table(T.group)
      .filter((r) => same(r.PROJECT_ID, pid))
      .map((r) => r.GROUP_ID);
    env.tables[T.group] = table(T.group).filter(
      (r) => !same(r.PROJECT_ID, pid),
    );
    for (const group of p.groups) {
      const gid = numericId(group.id) || next(T.group, "GROUP_ID");
      groupIds.set(group.id, gid);
      const old = (seed?.tables[T.group] || []).find((r) =>
        same(r.GROUP_ID, gid),
      );
      table(T.group).push({
        ...old,
        GROUP_ID: gid,
        PROJECT_ID: pid,
        GROUP_NAME: group.name,
        COLUMN_NUMBER: group.column,
        ROW_NUMBER: group.order,
        VISIBLE: group.visible,
        NOTE: group.note,
        INSTANCE_ID: group.instanceId
          ? instanceIds.get(group.instanceId)
          : null,
      });
    }
    env.tables[T.membership] = table(T.membership).filter(
      (r) =>
        !previousGroups.some((x) => same(x, r.GROUP_ID)) &&
        !ownBaseIds.some((x) => same(x, r.VARIABLE_ID)),
    );
    for (const field of p.fields.filter((f) => f.groupId)) {
      const vid = fieldIds.get(field.id),
        gid = groupIds.get(field.groupId!);
      const old = (seed?.tables[T.membership] || []).find(
        (r) => same(r.VARIABLE_ID, vid) && same(r.GROUP_ID, gid),
      );
      table(T.membership).push({
        ...old,
        GROUP_VARIABLE_ID:
          old?.GROUP_VARIABLE_ID ?? next(T.membership, "GROUP_VARIABLE_ID"),
        GROUP_ID: gid,
        VARIABLE_ID: vid,
        ROW_NUMBER: field.order,
      });
    }
    const previousTabs = table(T.tab)
      .filter((r) => same(r.PROJECT_ID, pid))
      .map((r) => r.TAB_ID);
    env.tables[T.tab] = table(T.tab).filter((r) => !same(r.PROJECT_ID, pid));
    for (const tab of p.tabs) {
      const tid = numericId(tab.id) || next(T.tab, "TAB_ID");
      tabIds.set(tab.id, tid);
      const old = (seed?.tables[T.tab] || []).find((r) => same(r.TAB_ID, tid));
      table(T.tab).push({
        ...old,
        TAB_ID: tid,
        PROJECT_ID: pid,
        TAB_NAME: tab.name,
        ROW_NUMBER: tab.order,
      });
    }
    env.tables[T.tabGroup] = table(T.tabGroup).filter(
      (r) => !previousTabs.some((x) => same(x, r.TAB_ID)),
    );
    for (const tab of p.tabs)
      for (const [order, group] of tab.groupIds.entries()) {
        const tid = tabIds.get(tab.id),
          gid = groupIds.get(group);
        const old = (seed?.tables[T.tabGroup] || []).find(
          (r) => same(r.TAB_ID, tid) && same(r.GROUP_ID, gid),
        );
        table(T.tabGroup).push({
          ...old,
          TAB_GROUPS_ID:
            old?.TAB_GROUPS_ID ?? next(T.tabGroup, "TAB_GROUPS_ID"),
          TAB_ID: tid,
          GROUP_ID: gid,
          ROW_NUMBER: order,
        });
      }
    env.tables[T.mapping] = table(T.mapping).filter(
      (r) => !priorInstances.some((x) => same(x, r.INSTANCE_ID)),
    );
    for (const instance of p.formInstances)
      for (const [struct, field] of Object.entries(instance.mapping)) {
        table(T.mapping).push({
          INSTANCE_ID: instanceIds.get(instance.id),
          VARIABLE_ID: fieldIds.get(field),
          STRUCT_ID: formStructIds.get(instance.formId)?.get(struct),
        });
      }
    const priorTriggers = table(T.trigger).filter((r) =>
      same(r.PROOFING_PROJECT_ID, pid),
    );
    env.tables[T.trigger] = table(T.trigger).filter(
      (r) => !same(r.PROOFING_PROJECT_ID, pid),
    );
    for (const sourceId of new Set(p.triggers.map((t) => t.sourceId))) {
      const vid = fieldIds.get(sourceId),
        old = priorTriggers.find((r) => same(r.VARIABLE_ID, vid)),
        oldJson = parseJSON(old?.TRIGGER_JSON, {}) as LegacyRow;
      const target = (target: string) =>
        groupIds.has(target)
          ? `group_${groupIds.get(target)}`
          : str(fieldIds.get(target));
      const rules = p.triggers
        .filter((t) => t.sourceId === sourceId)
        .map((t) => ({
          value: t.value,
          show: t.show.map(target),
          hide: t.hide.map(target),
          novalue: t.clear.map(target),
          dataSources: Object.entries(t.exclude).map(([field, values]) => ({
            variableId: fieldIds.get(field),
            values,
          })),
        }));
      table(T.trigger).push({
        ...old,
        ID: old?.ID ?? next(T.trigger, "ID"),
        PROOFING_PROJECT_ID: pid,
        VARIABLE_ID: vid,
        ACTIVE: true,
        CREATED_BY: old?.CREATED_BY ?? 0,
        TRIGGER_JSON: JSON.stringify({ ...oldJson, rules }),
      });
    }
    env.tables[T.linkage] = table(T.linkage).filter(
      (r) => !ownBaseIds.some((x) => same(x, r.VARIABLE_ID)),
    );
    for (const selector of p.fields.filter((f) => f.type === "preset")) {
      const targets = new Set(
        selector.presets.flatMap((preset) => Object.keys(preset.values)),
      );
      for (const key of targets) {
        const field = p.fields.find((f) => f.id === key || f.key === key);
        if (field)
          table(T.linkage).push({
            VARIABLE_LINKAGE_ID: next(T.linkage, "VARIABLE_LINKAGE_ID"),
            VARIABLE_ID: fieldIds.get(selector.id),
            TARGET_VARIABLE_ID: fieldIds.get(field.id),
          });
      }
    }
    // Native visual layouts and fields not representable in Pageflex rows are a versioned sidecar.
    p.id = id("project", pid);
    for (const f of p.fields) {
      const row = table(T.variable).find(
        (r) => same(r.VARIABLE_ID, fieldIds.get(f.id)) && r.PART_ID == null,
      );
      if (row) f.legacy = structuredClone(row);
    }
    const remapSource = (source: Field["source"]) => ({
      ...source,
      datasetId:
        source.datasetId && datasetIds.has(source.datasetId)
          ? id("dataset", datasetIds.get(source.datasetId))
          : source.datasetId,
      structId:
        source.structId && structsForSource(source).has(source.structId)
          ? id("struct", structsForSource(source).get(source.structId))
          : source.structId,
      instanceId:
        source.instanceId && instanceIds.has(source.instanceId)
          ? id("instance", instanceIds.get(source.instanceId))
          : source.instanceId,
    });
    for (const field of p.fields) {
      field.source = remapSource(field.source);
      if (field.groupId)
        field.groupId = id("group", groupIds.get(field.groupId));
    }
    for (const part of p.parts)
      for (const patch of Object.values(part.overrides)) {
        if (patch.source) patch.source = remapSource(patch.source);
        if (patch.groupId)
          patch.groupId = id("group", groupIds.get(patch.groupId));
      }
    for (const dataset of p.datasets)
      dataset.id = id("dataset", datasetIds.get(dataset.id));
    for (const form of p.forms) {
      const scoped = formStructIds.get(form.id)!;
      if (form.profileLibrary)
        form.profileLibrary.inputIds = Object.fromEntries(
          form.fields.map((field) => [
            id("struct", scoped.get(field.id)),
            form.profileLibrary!.inputIds?.[field.id] || field.id,
          ]),
        );
      form.id = id("form", formIds.get(form.id));
      for (const field of form.fields)
        field.id = id("struct", scoped.get(field.id));
    }
    for (const instance of p.formInstances) {
      const scoped = formStructIds.get(instance.formId)!;
      if (instance.inputOnly)
        instance.inputOnly = instance.inputOnly.map((input) =>
          id("struct", scoped.get(input)),
        );
      instance.id = id("instance", instanceIds.get(instance.id));
      instance.formId = id("form", formIds.get(instance.formId));
      instance.mapping = Object.fromEntries(
        Object.entries(instance.mapping).map(([struct, field]) => [
          id("struct", scoped.get(struct)),
          field,
        ]),
      );
    }
    for (const group of p.groups) {
      group.id = id("group", groupIds.get(group.id));
      if (group.instanceId)
        group.instanceId = id("instance", instanceIds.get(group.instanceId));
    }
    for (const tab of p.tabs) {
      tab.id = id("tab", tabIds.get(tab.id));
      tab.groupIds = tab.groupIds.map((group) =>
        id("group", groupIds.get(group)),
      );
    }
    for (const trigger of p.triggers) {
      const remap = (target: string) =>
        groupIds.has(target) ? id("group", groupIds.get(target)) : target;
      trigger.show = trigger.show.map(remap);
      trigger.hide = trigger.hide.map(remap);
      trigger.clear = trigger.clear.map(remap);
    }
    const provenance = {
      ...(p.legacy?.compositionSource
        ? { compositionSource: structuredClone(p.legacy.compositionSource) }
        : {}),
      ...(p.legacy?.documentImport
        ? { documentImport: structuredClone(p.legacy.documentImport) }
        : {}),
    };
    if (Object.keys(provenance).length) p.legacy = provenance;
    else delete p.legacy;
    native.push(p);
  }
  for (const project of native) {
    const row = env.tables[T.project].find((row) =>
      same(
        row.PROJECT_ID,
        project.legacy?.projectRow?.PROJECT_ID ??
          Number(project.id.replace(/^project_/, "")),
      ),
    );
    if (row)
      row.extra_json_data = JSON.stringify({
        ...(parseJSON(row.extra_json_data, {}) as LegacyRow),
        proofStudioVersion: 1,
        project,
      });
  }
  env._proofStudio = { version: 1, projects: native };
  return env;
}
export function customizationToLegacy(
  project: Project,
  proof: {
    id: string;
    values: Values;
    partId?: string;
    comments?: string;
    accepted?: boolean;
    initials?: string;
    name?: string;
    at?: string;
  },
  stateId: number,
  original: LegacyRow = {},
): LegacyEnvelope {
  return exportCustomization(
    project,
    {
      id: proof.id,
      projectId: project.id,
      partId: proof.partId,
      values: proof.values,
      name: proof.name || project.name,
      comments: proof.comments || "",
      accepted: !!proof.accepted,
      initials: proof.initials || "",
      at: proof.at || new Date().toISOString(),
    },
    {
      mode: "portable",
      stateId,
      mainPartId:
        typeof original.PART_ID === "number" ? original.PART_ID : undefined,
      complete: !!proof.accepted,
    },
    {
      tables: {
        ESM_CUSTOMIZATION_STATE: [
          { ...original, CUSTOMIZATION_STATE_ID: stateId },
        ],
      },
    },
  );
}
